import {
  AEMC_Object,
  AEMC_Event,
  AEMC_Method,
  AEMC_Attribute,
  ADMC_Attribute,
  AEMC_Dependency,
  AEMC_Transition,
  AEMC_MultiplePropagationConstraint,
  ADMC_MultiplePropagationConstraint,
  AEMC_FSM,
  AEMC_State,
  AEMC_SpecialiseEvent,
  AEMC_SpecialiseMethod,
  AEMC_ConvertToOwnedMethod,
  AEMC_ConvertToNormalEvent,
} from '../atomic';
import {
  NameCheck,
  DependencyNameCheck,
  MPCConflictCheck,
  ObjectInheritanceCheck,
  OwnedMethodEventOwningCheck,
  ObjectAttributeNameConflictCheck,
} from '../../../checks/specific';
import {
  ObjectDuplicateCheck,
  EventDuplicateCheck,
  MethodDuplicateCheck,
  AttributeDuplicateCheck,
  DependencyDuplicateCheck,
  MPCDuplicateCheck,
} from '../../../checks/duplicates';
import {
  AcquiredMethodConsistencyCheck,
  TransitionConsistencyCheck,
  MPCConsistencyCheck,
  EventConsistencyCheck,
  SpecialisedMethodConsistencyCheck,
} from '../../../checks/consistency';
import {
  ensureObject,
  ensureAttribute,
  ensureEvent,
  ensureMethod,
  ensureDependency,
  ensureFSM,
  ensureTransition,
  ensureMPC,
  ensureState,
  ensureInheritance,
  isObjectType,
} from '../util';
import { CCMC_DataType, CCMC_Attribute, CCMC_MultiplePropagationConstraint } from './create';
import { Combined_CreateTransition, Combined_DeleteTransition } from '../../combined';
import { upperFirst, uniq } from 'lodash';
import type { IModel } from '../../types';
import type {
  AnyCheck,
  AttributeOwnerLike,
  AttributeUpdates,
  DependencyUpdates,
  EventUpdates,
  FSMUpdates,
  MethodUpdates,
  MPCType,
  MPCUpdates,
  ObjectUpdates,
  OrId,
  PropagateRename,
} from '../../types';
import type { IObjectType } from '../../../../stores/model/metamodel/object_type';
import type { IEventType } from '../../../../stores/model/metamodel/event_type';
import type { IMethod } from '../../../../stores/model/metamodel/method';
import type { IDependency } from '../../../../stores/model/metamodel/dependency';
import type { IInheritance } from '../../../../stores/model/metamodel/inheritance';
import type { IFSM } from '../../../../stores/model/metamodel/fsm';
import type { IState } from '../../../../stores/model/metamodel/state';
import type { ITransition } from '../../../../stores/model/metamodel/transition';
import type { IDataType } from '../../../../stores/model/metamodel/data_type';
import type { IAttribute } from '../../../../stores/model/metamodel/attribute';
import type { IMultiplePropagationConstraint } from '../../../../stores/model/metamodel/multiple_propagation_constraint';

/**
 * Utility to apply updates to the attributes of the given attribute owner.
 *
 * @param {Model} model The model to operate on.
 * @param {AttributeOwner} owner The owner of the attributes.
 * @param {Object} attributeUpdates The updates to apply:
 * - {Array[String]} removals: ids of the attributes to remove.
 * - {Array[Object]} additions: the attributes to add (see CCMC_Attribute for
 *   the details of the arguments).
 * - {Array[Object]} edits: the attributes to edit (see CEMC_Attribute for the
 *   details of the arguments)
 */
function applyAttributeUpdates(
  model: IModel,
  owner: AttributeOwnerLike,
  attributeUpdates: AttributeUpdates | undefined
) {
  const checks: AnyCheck[] = [];
  const attributes: IAttribute[] = [];
  const dataTypes: IDataType[] = [];

  // Call ADMC_Attribute for the remove attributes
  if (attributeUpdates && attributeUpdates.removals) {
    attributeUpdates.removals.forEach((a) => {
      const attribute = ensureAttribute(owner, a)!;
      ADMC_Attribute(owner, attribute);
      // No checks needed
    });
  }

  // Call CCMC_Attribute for the additions
  if (attributeUpdates && attributeUpdates.additions) {
    attributeUpdates.additions.forEach(
      ({ name, typeName, description, implementation, validation }) => {
        const {
          checks: extraChecks,
          attribute,
          dataType,
        } = CCMC_Attribute(model, owner, name, typeName, description, implementation, validation);
        checks.push(...extraChecks);
        attributes.push(attribute);
        if (dataType) {
          dataTypes.push(dataType);
        }
      }
    );
  }

  // Call CEMC_Attribute for the edits
  if (attributeUpdates && attributeUpdates.edits) {
    attributeUpdates.edits.forEach(
      ({ id, name, typeName, description, implementation, validation }) => {
        const { checks: extraChecks, dataType } = CEMC_Attribute(
          model,
          owner,
          id,
          name,
          typeName,
          description,
          implementation,
          validation
        );
        checks.push(...extraChecks);
        if (dataType) {
          dataTypes.push(dataType);
        }
      }
    );
  }

  return {
    checks,
    attributes,
    dataTypes,
  };
}

/**
 * Utility to apply updates to the multiple propagation constraints of the given
 * object type.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType} object The object type.
 * @param {Object} mpcUpdates The updates to apply:
 * - {Array[String]} removals: ids of the multiple propagation constraints to
 *   remove.
 * - {Array[Object]} additions: the multiple propagation constraints to add (see
 *   CCMC_MultiplePropagationConstraint for the details of the arguments).
 * - {Array[Object]} edits: the multiple propagation constraints to edit (see
 *   CEMC_MultiplePropagationConstraint for the details of the arguments)
 */
function applyMPCUpdates(
  model: IModel,
  object: IObjectType,
  mpcUpdates: MPCUpdates | undefined
) {
  const checks: AnyCheck[] = [];
  const multiplePropagationConstraints: IMultiplePropagationConstraint[] = [];

  // Call ADMC_MultiplePropagationConstraint for the removed MPCs
  if (mpcUpdates && mpcUpdates.removals) {
    mpcUpdates.removals.forEach((m) => {
      const mpc = ensureMPC(object, m)!;
      ADMC_MultiplePropagationConstraint(object, mpc);
      // No checks needed
    });
  }

  // Call CCMC_MultiplePropagationConstraint for the additions
  if (mpcUpdates && mpcUpdates.additions) {
    mpcUpdates.additions.forEach(({ type, primaryPath, secondaryPath }) => {
      const { checks: extraChecks, multiplePropagationConstraint } =
        CCMC_MultiplePropagationConstraint(model, object, type, primaryPath, secondaryPath);
      checks.push(...extraChecks);
      multiplePropagationConstraints.push(multiplePropagationConstraint);
    });
  }

  // Call CEMC_MultiplePropagationConstraint for the edits
  if (mpcUpdates && mpcUpdates.edits) {
    mpcUpdates.edits.forEach(({ id, type }) => {
      const { checks: extraChecks } = CEMC_MultiplePropagationConstraint(model, object, id, { type });
      checks.push(...extraChecks);
    });
  }

  return {
    checks,
    multiplePropagationConstraints,
  };
}

/**
 * Edit an object type.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object type or its id.
 * @param {Object} updates The updates to the object type.
 * - {String} name: The new name
 * - {String} description: The new description
 * - {String} constraints: The new constraints
 * - {Object} attributes: The updates the attributes (see applyAttributeUpdates
 *   above)
 * @param {Object} propagateRename If the name changed, should we automatically:
 * - {Boolean} eventsAndMethods: rename events and methods too?
 * - {Boolean} dependencies: rename dependencies too?
 */
export function CEMC_Object(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  {
    name,
    description,
    constraints,
    attributes: attributeUpdates,
    multiplePropagationConstraints: mpcUpdates,
    abstract,
  }: ObjectUpdates,
  propagateRename: PropagateRename | boolean | null
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const object = ensureObject(metamodel, objectOrId)!;

  const oldName = object.name;
  const oldAbstract = object.abstract;

  // Allow to only partially pass updates
  name = name != null ? name : object.name;
  description = description != null ? description : object.description;
  constraints = constraints != null ? constraints : object.constraints;
  abstract = abstract != null ? abstract : object.abstract;

  // Update the object type
  AEMC_Object(object, name, description, constraints, abstract);

  if (oldAbstract !== abstract) {
    checks.push(new ObjectInheritanceCheck(model.metamodel, object));
  }

  if (oldName !== name) {
    // Name has changed, add checks for it.
    checks.push(new NameCheck(metamodel, object), new ObjectDuplicateCheck(metamodel, object));
    if (propagateRename != null && (propagateRename as PropagateRename).eventsAndMethods) {
      // We should propagate the rename to events and methods: find all events
      // that are named EVcrOldname or EVendOldname
      const nameRegexp = new RegExp(`^(EV_?cr_?|EV_?end_?)${oldName}$`, 'i');
      const eventsToRename = metamodel.eventTypes.filter((et) => nameRegexp.test(et.name));
      // Rename all of them (and ask CEMC_Event to propagate the rename to
      // methods)
      for (const et of eventsToRename) {
        const newName = et.name.replace(nameRegexp, (match, p1) => `${p1}${upperFirst(name)}`);
        const { checks: extraChecks } = CEMC_Event(model, et, { name: newName }, true);
        // this will not add any attributes or datatypes: we can ignore those
        // return values from CEMC_Event
        checks.push(...extraChecks);
      }
    }
    const renamedDependencies: IDependency[] = [];
    if (propagateRename != null && (propagateRename as PropagateRename).dependencies) {
      // We should propagate the rename to dependencies
      const depNameRegexp = new RegExp(oldName, 'i');
      for (const dep of object.dependenciesAsMaster) {
        // find dependencies as master to rename
        if (depNameRegexp.test(dep.name.master)) {
          const newDepName = dep.name.master.replace(depNameRegexp, name);
          const { checks: extraChecks } = CEMC_Dependency(model, dep, {
            name: { master: newDepName },
          });
          checks.push(...extraChecks);
          renamedDependencies.push(dep);
        }
      }
      for (const dep of object.dependenciesAsDependent) {
        // find dependencies as dependent to rename
        if (depNameRegexp.test(dep.name.dependent)) {
          const newDepName = dep.name.dependent.replace(depNameRegexp, name);
          const { checks: extraChecks } = CEMC_Dependency(model, dep, {
            name: { dependent: newDepName },
          });
          checks.push(...extraChecks);
          renamedDependencies.push(dep);
        }
      }
    }
    // By renaming, we can introduce a conflict with an attribute of our dependents/masters (or their subtypes).
    // Only add the checks when the dependency has not been renamed, otherwise we get duplicate checks.
    for (const attribute of uniq(
      object.dependencies.flatMap((dependency) => {
        if (!renamedDependencies.includes(dependency)) {
          const other = dependency.master === object ? dependency.dependent : dependency.master;
          return other.attributes.concat(
            other.indirectSubtypes.flatMap((st) => st.attributes.slice())
          );
        } else {
          return [];
        }
      })
    )) {
      checks.push(new ObjectAttributeNameConflictCheck(metamodel, attribute));
    }
  }

  // Apply the attribute updates.
  const {
    checks: extraChecks,
    attributes,
    dataTypes,
  } = applyAttributeUpdates(model, object, attributeUpdates);
  checks.push(...extraChecks);

  // Apply the mpc updates.
  const { checks: mpcChecks, multiplePropagationConstraints } = applyMPCUpdates(
    model,
    object,
    mpcUpdates
  );
  checks.push(...mpcChecks);

  return {
    checks,
    attributes,
    dataTypes,
    multiplePropagationConstraints,
  };
}

/**
 * Edit an attribute.
 *
 * @param {Model} model The model to operate on.
 * @param {AttributeOwner} owner The attribute owner.
 * @param {Attribute or String} attributeOrId The attribute to edit or its id.
 * The parameters below are optional, they will be set to the old value if
 * omitted.
 * @param {String} name The new name.
 * @param {String} typeName The new data type name.
 * @param {String} description The new description.
 * @param {String} implementation The new implementation.
 * @param {String} validation The new validation.
 */
export function CEMC_Attribute(
  model: IModel,
  owner: AttributeOwnerLike,
  attributeOrId: OrId<IAttribute>,
  name?: string,
  typeName?: string,
  description?: string,
  implementation?: string,
  validation?: string
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const attribute = ensureAttribute(owner, attributeOrId)!;
  name = name || attribute.name;
  typeName = typeName || attribute.typeName;
  description = description || attribute.description;
  implementation = implementation || attribute.implementation;
  validation = validation || attribute.validation;

  // First determine the data type
  let dataType: IDataType | undefined;
  let typeArg: string;
  if (metamodel.isBuiltinDataType(typeName)) {
    // It's a builtin, ACMC_Attribute expects the name.
    typeArg = typeName;
  } else {
    // It's not a builtin, see if it is an existing custom data type.
    dataType = metamodel.getDataTypeByName(typeName);
    if (!dataType) {
      // No existing data type found, create one
      const { checks: extraChecks, dataType: newDataType } = CCMC_DataType(model, typeName);
      dataType = newDataType;
      checks.push(...extraChecks);
    }
    // For a custom data type, ACMC_Attribute expects the id.
    typeArg = dataType.id;
  }

  // Store the value of the old name
  const oldName = attribute.name;

  // Update the attribute
  AEMC_Attribute(attribute, name, typeArg, description, implementation, validation);

  if (oldName !== name) {
    // The name changed. Add relevant checks.
    checks.push(
      new NameCheck(metamodel, attribute),
      new AttributeDuplicateCheck(metamodel, attribute)
    );
    if (isObjectType(metamodel, owner)) {
      checks.push(new ObjectAttributeNameConflictCheck(metamodel, attribute));
    }
  }

  return {
    checks,
    dataType,
  };
}

/**
 * Edit an event type.
 *
 * @param {Model} model The model to operate on.
 * @param {EventType or String} eventOrId The event or its id.
 * @param {Object} updates The updates
 * - {String} name: the new name (optional)
 * - {String} description: the new description (optional)
 * - {Object} attributes: the updates to the attributes (see
 *   applyAttributeUpdate above) (optional)
 * @param {Boolean} propagateRename If the name changed, should we also rename
 * related methods?
 */
export function CEMC_Event(
  model: IModel,
  eventOrId: OrId<IEventType>,
  { name, description, attributes: attributeUpdates }: EventUpdates,
  propagateRename: boolean
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const event = ensureEvent(metamodel, eventOrId)!;
  name = name || event.name;
  description = description || event.description;

  // Store the old name
  const oldName = event.name;

  // Edit the event.
  AEMC_Event(event, name, description);
  if (oldName !== name) {
    // Name has changed, add relevant checks.
    checks.push(new NameCheck(metamodel, event), new EventDuplicateCheck(metamodel, event));
    if (propagateRename) {
      // Also rename the methods
      const newMethodName = name.replace(/^EV/i, 'ME');
      for (const m of event.methods) {
        const { checks: extraChecks } = CEMC_Method(model, m, {
          name: newMethodName,
        });
        // this will not add any attributes or datatypes: we can ignore those
        // return values from CEMC_Method
        checks.push(...extraChecks);
      }
    }
  }

  // Apply the attribute updates
  const {
    checks: extraChecks,
    attributes,
    dataTypes,
  } = applyAttributeUpdates(model, event, attributeUpdates);
  checks.push(...extraChecks);

  return {
    checks,
    attributes,
    dataTypes,
  };
}

/**
 * Convert an event type to a specialised event.
 *
 * @param {Model} model The model to operate on.
 * @param {EventType or String} eventOrId The event or its id.
 * @param {EventType or String} ownerEventTypeOrId: the new owner event type or its id.
 * @param {Inheritance or String} inheritanceOrId: the new inheritance or its id.
 */
export function CEMC_SpecialiseEvent(
  model: IModel,
  eventOrId: OrId<IEventType>,
  ownerEventTypeOrId: OrId<IEventType>,
  inheritanceOrId: OrId<IInheritance>
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const event = ensureEvent(metamodel, eventOrId)!;
  const ownerEventType = ensureEvent(metamodel, ownerEventTypeOrId)!;
  const inheritance = ensureInheritance(metamodel, inheritanceOrId)!;

  // Specialise the event
  AEMC_SpecialiseEvent(event, ownerEventType, inheritance);
  checks.push(new EventConsistencyCheck(metamodel, event));

  return {
    checks,
    event,
  };
}

/**
 * Convert a specialised event type to a normal event.
 *
 * @param {Model} model The model to operate on.
 * @param {EventType or String} eventOrId The event or its id.
 */
export function CEMC_ConvertToNormalEvent(model: IModel, eventOrId: OrId<IEventType>) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const event = ensureEvent(metamodel, eventOrId)!;

  // Convert the event to a normal event
  AEMC_ConvertToNormalEvent(event);
  checks.push(new EventConsistencyCheck(metamodel, event));

  return {
    checks,
    event,
  };
}

/**
 * Helper method to remove a method from a transition
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType} owner The owner object type.
 * @param {FSM} fsm The FSM to remove the transition from.
 * @param {Method} method the method to remove
 * @param {Transition} transition the transition to remove the method from
 */
function removeMethodFromTransition(
  model: IModel,
  owner: IObjectType,
  fsm: IFSM,
  method: IMethod,
  transition: ITransition
) {
  const newMethods = transition.metaMethodIds.filter((id) => id !== method.id);
  if (newMethods.length > 0) {
    return CEMC_Transition(
      model,
      owner,
      fsm,
      transition,
      transition.metaMethodIds.filter((id) => id !== method.id)
    );
  } else {
    return Combined_DeleteTransition(model, owner, fsm, transition);
  }
}

/**
 * Edit a method.
 *
 * @param {Model} model The model to operate on.
 * @param {Method or String} methodOrId The method or its id.
 * @param {Object} updates The updates.
 *  - {String} name: the new name (optional)
 *  - {METHOD_TYPE} type: the new type (optional)
 *  - {String} precondition: the new precondition (optional)
 *  - {String} implementation: the new implementation (optional)
 *  - {String} postcondition: the new postcondition (optional)
 *  - {Object} attributes: the updates to the attributes (see
 *    applyAttributeUpdates above) (optional)
 */
export function CEMC_Method(
  model: IModel,
  methodOrId: OrId<IMethod>,
  {
    name,
    type,
    precondition,
    implementation,
    postcondition,
    attributes: attributeUpdates,
  }: MethodUpdates
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];
  const method = ensureMethod(metamodel, methodOrId)!;
  name = name || method.name;
  type = type || method.type;
  precondition = precondition || method.precondition;
  implementation = implementation || method.implementation;
  postcondition = postcondition || method.postcondition;

  // Store the old name.
  const oldName = method.name;
  const oldType = method.type;
  // Update the method
  AEMC_Method(method, name, type, precondition, implementation, postcondition);
  if (oldName !== name) {
    // Name changed, add the relevant checks.
    checks.push(new NameCheck(metamodel, method), new MethodDuplicateCheck(metamodel, method));
  }
  if (method.provenance === 'ACQUIRED') {
    // The method is acquired, add consistency check
    checks.push(new AcquiredMethodConsistencyCheck(metamodel, method));
  } else if (method.provenance === 'OWNED') {
    // The method is owned, add consistency checks for all its acquired methods
    checks.push(
      ...method.findAcquiredMethods().map((m) => new AcquiredMethodConsistencyCheck(metamodel, m))
    );
  }

  if (type !== oldType) {
    // Move the method to the correct transition in the default FSM
    const owner = method.ownerObjectType;
    const fsm = owner.defaultFSM!;

    // Remove the method from the original transition
    let origSource = fsm.initialState;
    let origTarget = fsm.finalStates[0]; // default FSM only has 1 final state
    if (oldType !== 'CREATE') {
      origSource = fsm.intermediaryStates[0]; // default FSM only has 1 intermediary state
    }
    if (oldType !== 'END') {
      origTarget = fsm.intermediaryStates[0]; // default FSM only has 1 intermediary state
    }
    const origTransition = fsm.findTransition(origSource!, origTarget!);

    if (origTransition != null) {
      // Only remove the method from its original transition when that transition
      // actually exists. It can legitimately be absent — e.g. the method was
      // never added to the default FSM (issue #51 scenario) — in which case there
      // is nothing to remove and dereferencing `origTransition` would throw
      // (issue #52, originally a UI hang). Fall through to the add-to-new-transition
      // branch below regardless.
      const { checks: oldTransitionChecks } = removeMethodFromTransition(
        model,
        owner,
        fsm,
        method,
        origTransition
      );
      checks.push(...oldTransitionChecks);
    }

    // Add to the new transition
    let source = fsm.initialState;
    let target = fsm.finalStates[0]; // default FSM only has 1 final state
    if (type !== 'CREATE') {
      source = fsm.intermediaryStates[0]; // default FSM only has 1 intermediary state
    }
    if (type !== 'END') {
      target = fsm.intermediaryStates[0]; // default FSM only has 1 intermediary state
    }
    const transition = fsm.findTransition(source!, target!);
    if (transition != null) {
      const { checks: newTransitionChecks } = CEMC_Transition(
        model,
        owner,
        fsm,
        transition,
        transition.metaMethodIds.concat([method.id])
      );
      checks.push(...newTransitionChecks);
    } else {
      const { checks: newTransitionChecks } = Combined_CreateTransition(
        model,
        owner,
        fsm,
        source!,
        target!,
        [method.id]
      );
      checks.push(...newTransitionChecks);
    }

    // Remove from any transition in non-default FSMs
    const nonDefaultFSMs = owner.fsms.filter((f) => f.type !== 'DEFAULT');
    for (const fsm of nonDefaultFSMs) {
      for (const transition of fsm.findTransitionsForMethod(method)) {
        const { checks: additionalChecks } = removeMethodFromTransition(
          model,
          owner,
          fsm,
          method,
          transition
        );
        checks.push(...additionalChecks);
      }
    }
  }

  // Apply the attribute updates
  const {
    checks: extraChecks,
    attributes,
    dataTypes,
  } = applyAttributeUpdates(model, method, attributeUpdates);
  checks.push(...extraChecks);

  return {
    checks,
    attributes,
    dataTypes,
  };
}

/**
 * Convert a method to a specialised method.
 *
 * @param {Model} model The model to operate on.
 * @param {Method or String} methodOrId The method or its id.
 * @param {Method or String} viaMethodOrId The via method or its id.
 */
export function CEMC_SpecialiseMethod(
  model: IModel,
  methodOrId: OrId<IMethod>,
  viaMethodOrId: OrId<IMethod>
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const method = ensureMethod(metamodel, methodOrId)!;
  const viaMethod = ensureMethod(metamodel, viaMethodOrId)!;

  AEMC_SpecialiseMethod(method, viaMethod);
  checks.push(new SpecialisedMethodConsistencyCheck(metamodel, method));

  return {
    checks,
  };
}

/**
 * Convert a specialised method to an owned method.
 *
 * @param {Model} model The model to operate on.
 * @param {Method or String} methodOrId The method or its id.
 */
export function CEMC_ConvertToOwnedMethod(model: IModel, methodOrId: OrId<IMethod>) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const method = ensureMethod(metamodel, methodOrId)!;

  AEMC_ConvertToOwnedMethod(method);
  checks.push(
    new MethodDuplicateCheck(metamodel, method),
    new OwnedMethodEventOwningCheck(metamodel, method)
  );

  return {
    checks,
  };
}

/**
 * Edit a dependency.
 *
 * @param {Model} model The model to operate on.
 * @param {Dependency or String} dependencyOrId The dependency or its id.
 * @param {Object} updates The updates.
 *  - {Object} name: the new name
 *    - {String} master: the new master-side name (optional)
 *    - {String} dependent: the new dependent-side name (optional)
 *  - {DEPENDENCY_TYPE} type: the new type (optional)
 */
export function CEMC_Dependency(
  model: IModel,
  dependencyOrId: OrId<IDependency>,
  { name, type }: DependencyUpdates
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];
  const dependency = ensureDependency(metamodel, dependencyOrId)!;
  const newName = {
    master: name.master || dependency.name.master,
    dependent: name.dependent || dependency.name.dependent,
  };
  type = type || dependency.type;

  const oldMasterName = dependency.name.master;
  const oldDependentName = dependency.name.dependent;

  // Update the dependency
  AEMC_Dependency(dependency, newName, type);

  if (oldMasterName !== newName.master || oldDependentName !== newName.dependent) {
    // Name changed, add relevant checks
    checks.push(
      new DependencyNameCheck(metamodel, dependency),
      new DependencyDuplicateCheck(metamodel, dependency)
    );
  }
  if (oldMasterName !== newName.master) {
    // We might introduce conflicts with attributes of the dependent or its subtypes
    for (const attribute of dependency.dependent.attributes.concat(
      dependency.dependent.indirectSubtypes.flatMap((st) => st.attributes.slice())
    )) {
      checks.push(new ObjectAttributeNameConflictCheck(metamodel, attribute));
    }
  }
  if (oldDependentName !== newName.dependent) {
    // We might introduce conflicts with an attribute of the master of its subtypes
    for (const attribute of dependency.master.attributes.concat(
      dependency.master.indirectSubtypes.flatMap((st) => st.attributes.slice())
    )) {
      checks.push(new ObjectAttributeNameConflictCheck(metamodel, attribute));
    }
  }

  return {
    checks,
  };
}

/**
 * Edit a multiple propagation constraint. Only updating the type is supported.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object type.
 * @param {MultiplePropagationConstraint or String} mpcOrId The multiple
 * propagation constraint.
 * @param {Object} updates The updates.
 *  - {MPC_TYPE} type: the new type
 */
export function CEMC_MultiplePropagationConstraint(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  mpcOrId: OrId<IMultiplePropagationConstraint>,
  { type }: { type?: MPCType }
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const object = ensureObject(metamodel, objectOrId)!;
  const mpc = ensureMPC(object, mpcOrId)!;

  // Update the mpc
  if (type != null) {
    AEMC_MultiplePropagationConstraint(mpc, type);
  }

  // checks: MPCPathCheck is not needed as we don't allow to update the path
  checks.push(
    new MPCConsistencyCheck(model.metamodel, mpc),
    new MPCDuplicateCheck(model.metamodel, mpc),
    new MPCConflictCheck(model.metamodel, mpc)
  );

  return {
    checks,
  };
}

/**
 * Edit an FSM. Updating the name and the codegeneration selection are
 * supported.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object type.
 * @param {FSM or String} fsmOrId The FSM.
 * @param {Object} updates The updates.
 *  - {String} name: the new name (optional).
 *  - {Boolean} codegeneration: whether the FSM is selected for code generation
 *    (optional).
 */
export function CEMC_FSM(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>,
  { name, codegeneration }: FSMUpdates
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const object = ensureObject(metamodel, objectOrId)!;
  const fsm = ensureFSM(object, fsmOrId)!;

  const oldName = fsm.name;
  const oldCodeGeneration = fsm.codegeneration;

  // Change the FSM
  AEMC_FSM(fsm, name || oldName, codegeneration != null ? codegeneration : oldCodeGeneration);

  if (oldName !== fsm.name) {
    // Name changed
    checks.push(new NameCheck(metamodel, fsm));
  }

  return {
    checks,
  };
}

/**
 * Select an FSM for code generation.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object type.
 * @param {FSM or String} fsmOrId The FSM.
 */
export function CEMC_SelectFSM(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>
) {
  const metamodel = model.metamodel;

  const object = ensureObject(metamodel, objectOrId)!;
  const fsm = ensureFSM(object, fsmOrId)!;

  const currentlySelectedFSM = object.fsmForCodeGeneration!;

  const { checks: deselectChecks } = CEMC_FSM(model, object, currentlySelectedFSM, {
    codegeneration: false,
  });
  const { checks: selectChecks } = CEMC_FSM(model, object, fsm, { codegeneration: true });

  return {
    checks: [...deselectChecks, ...selectChecks],
  };
}

/**
 * Edit (the methods in) a transition.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType of String} objectOrId The object type.
 * @param {FSM or String} fsmOrId The FSM.
 * @param {Transition or String} transitionOrdId The transition.
 * @param {Method[] or String[]} methodsOrdIds The methods.
 */
export function CEMC_Transition(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>,
  transitionOrId: OrId<ITransition>,
  methodsOrIds: OrId<IMethod>[]
) {
  const checks: AnyCheck[] = [];

  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm = ensureFSM(object, fsmOrId)!;
  const transition = ensureTransition(fsm, transitionOrId)!;
  const methods = methodsOrIds.map((m) => ensureMethod(model.metamodel, m)!);

  // Update the transition
  AEMC_Transition(transition, methods);
  // NameCheck not needed as we don't allow to edit the name
  checks.push(new TransitionConsistencyCheck(model.metamodel, transition));

  return {
    checks,
  };
}

/**
 * Edit (the name of) a state in an FSM.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType of String} objectOrId The object type.
 * @param {FSM or String} fsmOrId The FSM.
 * @param {State or String} stateOrId The state.
 * @param {String} The new name.
 */
export function CEMC_State(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>,
  stateOrId: OrId<IState>,
  name: string
) {
  const checks: AnyCheck[] = [];

  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm = ensureFSM(object, fsmOrId)!;
  const state = ensureState(fsm, stateOrId)!;

  const oldName = state.name;

  // Update the state
  AEMC_State(state, name);

  // Add checks
  if (oldName !== name) {
    checks.push(new NameCheck(model.metamodel, state));
  }

  return {
    checks,
  };
}
